import { useCallback, useEffect, useState } from 'react';
import { useFindProximity } from '../../contexts/FindProximityContext';
import { permissionRows } from './PermissionsStatus';
import { markDone } from '../../lib/moments';
import { resolveEventDisplayName } from '../../utils/eventDisplayName';
import { formatEventMomentDay, formatEventMomentSoon } from '../../utils/eventDateFormat';

/**
 * Everything the radar needs, for the Find tab and for Find inside Maps (Sam, 2026-10-13: one Find, two doors). The
 * permissions sheet, the cases the radar can never recover from, and tap-to-track live here once so the two screens
 * cannot drift apart.
 *
 * `scenario` (dev only, lib/map/mapScenarios) stands in a session: its peers, its heading, and a local tracked id.
 */
export function useFindRadar({ scenario = null } = {}) {
  const proximity = useFindProximity();
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [scenarioTracked, setScenarioTracked] = useState(scenario?.trackedPeerId ?? null);

  useEffect(() => {
    if (proximity.locationAuthLost) setPermissionsOpen(true);
  }, [proximity.locationAuthLost]);

  const locationDenied =
    proximity.locationAuthLost ||
    proximity.locationAuth === 'denied' ||
    proximity.locationAuth === 'restricted';
  // Precise Location off: iOS hands out fixes kilometres wide, the publisher's accuracy gate drops them, and you are
  // invisible to your group with nothing on screen saying why (2026-10-16 audit). Needs the native status to carry
  // CLLocationManager.accuracyAuthorization (`accuracy: 'full' | 'reduced'`) and useProximity to pass it on as
  // `locationAccuracy`; until then this is always false.
  const preciseOff = proximity.locationAccuracy === 'reduced';
  // Location / Precise / Bluetooth, the same sheet as the map's (operator, 2026-10-04). No "Always" pitch:
  // Prii asks for While Using only.
  const permRows = permissionRows({
    locationAuth: proximity.locationAuth,
    locationLost: proximity.locationAuthLost === true,
    preciseOff,
    bleState: proximity.bleState,
  });

  // Nothing on yet but a show ahead: the radar names it and the page says when Find turns on, "at 4:30 PM" this week,
  // "Nov 14" beyond (Sam's pick C, 2026-11-01; "No group" read as if you had none).
  const nextRsvp = proximity.nextRsvp;
  const soon = nextRsvp ? formatEventMomentSoon(nextRsvp.event, nextRsvp.opensAt) : null;
  const turnsOn = soon
    ? `at ${soon}`
    : nextRsvp
      ? formatEventMomentDay(nextRsvp.event, nextRsvp.opensAt)
      : null;
  const upcoming =
    !scenario && turnsOn && !proximity.sessionEventId && !proximity.sessionLoading
      ? {
          name: resolveEventDisplayName(nextRsvp.event),
          turnsOn,
          eventId: nextRsvp.event.festival_parent_event_id ?? nextRsvp.event.event_id,
        }
      : null;

  /** The radar says "Connecting…" for everything; name the cases it can never recover from. */
  const blockedReason = scenario
    ? null
    : !proximity.isNative
      ? 'Open in the Prii app'
      : upcoming
        ? upcoming.name
        : !proximity.sessionEventId && !proximity.sessionLoading
          ? 'No shows' // Sam's pick A, 2026-11-01; and 2026-10-22: never end on a verb or adverb
          : locationDenied || proximity.locationAuth === 'notDetermined'
            ? 'Turn on location'
            : preciseOff
              ? 'Turn on Precise Location'
              : null;

  const trackedPeerId = scenario ? scenarioTracked : proximity.trackedPeerId;
  const firstTrack = () => markDone('track'); // the first-time path (lib/moments): Find's first person glows until then
  const handlePeerTap = useCallback(
    (peerId) => {
      if (scenario) {
        setScenarioTracked((was) => {
          if (was !== peerId) firstTrack();
          return was === peerId ? null : peerId;
        });
        return;
      }
      const wasTracked = proximity.trackedPeerId === peerId;
      if (!wasTracked) firstTrack();
      proximity.toggleTrackPeer(peerId);
      if (!wasTracked && proximity.isNative && (proximity.headingDenied || locationDenied))
        setPermissionsOpen(true);
    },
    [scenario, proximity, locationDenied],
  );

  const peers = scenario ? scenario.peers : proximity.peers;
  const radarProps = scenario
    ? {
        peers,
        trackedPeerId,
        compassReady: true,
        deviceHeadingDeg: scenario.headingDeg,
        headingNeedsTap: false,
        headingDenied: false,
        onPeerTap: handlePeerTap,
        onRequestCompass: () => {},
        subscriptionStatus: 'connected',
        livePeersSynced: true,
        peersHydration: 'live',
        isNative: false,
        blockedReason: null,
        sessionKey: 'scenario',
      }
    : {
        peers,
        trackedPeerId,
        trackedUwbReading: proximity.trackedUwbReading,
        compassReady: proximity.compassReady,
        onPeerTap: handlePeerTap,
        deviceHeadingDeg: proximity.deviceHeadingDeg,
        headingNeedsTap: proximity.headingNeedsTap,
        headingDenied: proximity.headingDenied,
        onRequestCompass: () => void proximity.requestCompassPermission(),
        subscriptionStatus: proximity.subscriptionStatus,
        livePeersSynced: proximity.livePeersSynced,
        peersHydration: proximity.peersHydration,
        isNative: proximity.isNative,
        blockedReason,
        sessionKey: proximity.sessionEventId
          ? `${proximity.solo ? 'solo' : 'event'}:${proximity.sessionEventId}`
          : null,
      };

  return {
    proximity,
    upcoming,
    peers,
    trackedPeerId,
    handlePeerTap,
    radarProps,
    permRows,
    permissionsOpen,
    setPermissionsOpen,
    locationDenied,
    onAskLocation: () => {
      void proximity.requestLocationWhenInUse?.();
    },
  };
}
